import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { ThemeService } from '@core/services/theme.service';
import { Theme } from '@support/models/theme.model';
import { AccessoryItem, ProductTypes, SubscriptionForHardwareOffering } from '@sales/models/accessory.model';
import { WarehouseStateModel } from '@sales/states/warehouse/warehouse.state';
import { IconNames } from '@shared/enums/icon-names';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { Observable, Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { select, Store } from '@ngxs/store';
import { CustomerProductSelectors } from '@app/sales/states/customer-product/customer-product.selectors';
import { ApiGetAllSubscriptionOfferingsAction } from '@app/sales/states/customer-product/customer-product.actions';
import { AppData } from '@app/shared/models/appdata.model';
import { FormHelperService } from '@app/core/services/form-helper.service';
import { PaymentMethod, PhoneNumberOption } from '@app/sales/modules/accessories/models/accessory-options.model';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { AsyncPipe } from '@angular/common';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { PhoneNumberOptionsComponent } from './phone-number-options/phone-number-options.component';
import { SubscriptionChooserComponent } from './subscription-chooser/subscription-chooser.component';
import { MatRadioGroup } from '@angular/material/radio';
import { MatIcon } from '@angular/material/icon';
import { AccessoryInformationComponent } from './accessory-information/accessory-information.component';

@Component({
  selector: 'app-accessory-details',
  templateUrl: './accessory-details.component.html',
  styleUrls: ['./accessory-details.component.scss'],
  imports: [
    AccessoryInformationComponent,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatRadioGroup,
    SubscriptionChooserComponent,
    PhoneNumberOptionsComponent,
    IceButtonComponent,
    SpinnerComponent,
    AsyncPipe,
    PriceFormatPipe,
  ],
})
export class AccessoryDetailsComponent implements OnInit {
  private themeService = inject(ThemeService);
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private appData = inject(AppData);
  private formHelper = inject(FormHelperService);

  IconNames = IconNames;
  PriceType = PriceType;

  @Input() accessoryItem: AccessoryItem;
  @Input() warehouseState: WarehouseStateModel;

  @Output() backToSearch = new EventEmitter();
  @Output() addAccessory = new EventEmitter();

  existingVoiceProducts = select(CustomerProductSelectors.getExistingVoiceProducts);

  accessoryForm: FormGroup;

  selectedTheme$: Observable<Theme>;

  PaymentMethod = PaymentMethod;

  selectedSubscription: SubscriptionForHardwareOffering = null;

  subscriptions$: Subscription[] = [];

  ngOnInit() {
    this.selectedTheme$ = this.themeService.selectedTheme;

    this.accessoryForm = this.fb.group({
      paymentMethod: ['', Validators.required],
      phoneNumberOption: ['', Validators.required],
      subscription: ['', Validators.required],
      subscriptionIdToUpgrade: ['', Validators.required],
    });

    this.subscriptions$.push(
      this.accessoryForm.get('paymentMethod').valueChanges.subscribe(() => {
        this.updateFormControls();
      })
    );

    this.subscriptions$.push(
      this.accessoryForm.get('phoneNumberOption').valueChanges.subscribe(() => {
        this.updateFormControls();
      })
    );
    this.store.dispatch(new ApiGetAllSubscriptionOfferingsAction());
  }

  updateFormControls() {
    const paymentMethodControl = this.accessoryForm.get('paymentMethod');
    const phoneNumberOptionControl = this.accessoryForm.get('phoneNumberOption');
    const subscriptionControl = this.accessoryForm.get('subscription');
    const subscriptionIdToUpgradeControl = this.accessoryForm.get('subscriptionIdToUpgrade');

    switch (paymentMethodControl.value) {
      case PaymentMethod.PayNow:
        this.formHelper.disableControls([subscriptionControl, subscriptionIdToUpgradeControl, phoneNumberOptionControl]);
        this.formHelper.setControlsValue(null, [
          subscriptionControl,
          subscriptionIdToUpgradeControl,
          phoneNumberOptionControl,
        ]);
        break;

      case PaymentMethod.PayWithSubscription:
        this.formHelper.enableControls([subscriptionControl, phoneNumberOptionControl]);
        switch (phoneNumberOptionControl.value) {
          case PhoneNumberOption.NewPhoneNumber:
            this.formHelper.disableControls([subscriptionIdToUpgradeControl]);
            this.formHelper.setControlsValue(null, [subscriptionIdToUpgradeControl]);
            break;

          case PhoneNumberOption.KeepExisting:
            this.formHelper.enableControls([subscriptionIdToUpgradeControl]);
            break;
        }
        break;
    }
  }

  onConfirm() {
    this.addAccessory.emit({
      accessoryItem: this.accessoryItem,
      subscription: this.selectedSubscription,
      phoneNumberOption: this.accessoryForm.get('phoneNumberOption').value,
      paymentType: this.accessoryForm.get('paymentMethod').value,
      subscriptionIdToUpgrade: this.accessoryForm.get('subscriptionIdToUpgrade').value,
    });
  }

  get availableSubscriptions(): SubscriptionForHardwareOffering[] {
    return this.accessoryItem.hardwareOfferingWithSubscriptionPrice?.availableSubscriptions;
  }

  onSubscriptionChange() {
    this.selectedSubscription = this.availableSubscriptions.find(
      subscription => subscription.subscriptionOfferingId === this.accessoryForm.get('subscription').value
    );
  }

  get webShopLink() {
    const { manufacturer, slug, groupSlug } = this.accessoryItem;

    if (!manufacturer || !slug) {
      return '';
    }

    if (
      this.accessoryItem.productType === ProductTypes.Smartwatch ||
      this.accessoryItem.productType === ProductTypes.CellphoneAccessory
    ) {
      return `${this.appData.webShopBaseUrl}/smartklokke/${manufacturer}/${groupSlug}/${slug}`;
    }

    return `${this.appData.webShopBaseUrl}/mobiltilbehor/${manufacturer}/${slug}`;
  }

  get isAccessoryBindingAvailable(): boolean {
    return !!this.accessoryItem.hardwareOfferingWithSubscriptionPrice;
  }

  get quantityInStock(): string | number {
    const quantity = this.warehouseState.quantityInStock[this.accessoryItem.offeringId];

    if (quantity > 20) {
      return '20+';
    } else if (quantity >= 5) {
      return quantity;
    } else if (quantity > 0) {
      return 'Færre enn 5';
    }
  }

  get isOutOfStock(): boolean {
    return !(this.hasStock || this.canBeBackOrdered);
  }

  get canBeBackOrdered(): boolean {
    return !this.hasStock && !!this.accessoryItem.backorderInfo?.allow;
  }

  private get hasStock(): boolean {
    return this.warehouseState.quantityInStock[this.accessoryItem.offeringId] > 0;
  }
}
